/** @jsxImportSource @emotion/react */
import * as ReactDOM from "react-dom";
import { useEffect, useState } from "react";
import { css } from "@emotion/react";
import { useSelector } from "react-redux";
import { IAppState } from "../../core/redux/IAppState";
import { getResource } from "../../language/resources/getResource";
import * as Buttons from "../../forms/elements/buttons";
import { FADE_IN_ANIMATION_SHORT } from "../../styles/animations";
import { LAYER } from "../zIndex";
import {
  BLUE_CREEK,
  COOL_BLUE,
  COOL_GREY,
  DARK_GREY,
  IKON_BLUE,
} from "../../styles/colors";
import { Popout } from "./openPopout";
import { setPageLockedByPopout } from "./popoutLock";

/**
 * Tells the tab a dialog was detached from where the dialog went, and raises the
 * page lock while it's away (see `popoutLock`). Covers and dims nothing — reading
 * the list the dialog is acting on is the reason for detaching.
 */

/** A page offers a home for the notice by rendering an element with this id. */
export const POPOUT_NOTICE_SLOT_ID = "popout-notice-slot";

// A tinted strip: fits an existing toolbar row instead of adding a band of its own,
// and reads as information rather than an alert. Wraps where the row is narrow.
const noticeCss = css`
  ${FADE_IN_ANIMATION_SHORT};
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 16px;
  background: ${COOL_BLUE};
  border: 1px solid ${COOL_GREY};
  border-radius: 6px;
  padding: 8px 14px;
  box-sizing: border-box;
`;

// Fallback for a page with no slot. Floats, so it covers content — hence the slot.
const floatingCss = css`
  position: fixed;
  right: 20px;
  bottom: 20px;
  width: 340px;
  max-width: calc(100vw - 40px);
  z-index: ${LAYER.TWO.MODAL_BASE};
  box-shadow: 0 4px 16px rgb(0 0 0 / 18%);
`;

const textCss = css`
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;
`;

// The same glyph as the Pop out button, tying the notice to the action behind it.
const glyphCss = css`
  font-size: 15px;
  line-height: 1;
  color: ${BLUE_CREEK};
  flex: 0 0 auto;
`;

const headingCss = css`
  font-size: 13px;
  font-weight: 700;
  color: ${IKON_BLUE};
`;

const bodyCss = css`
  font-size: 12px;
  color: ${DARK_GREY};
`;

const buttonsCss = css`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
`;

type Props = {
  popout: Popout;
  /** The popped-out dialog's title, named in the notice. */
  title: string;
  /** Bring the dialog back into this tab — the window's own Dock action. */
  onDock: () => void;
};

export function PopoutBlocker({ popout, title, onDock }: Props) {
  const resources = useSelector((s: IAppState) => s.locale);

  useEffect(() => {
    setPageLockedByPopout(true);
    return () => setPageLockedByPopout(false);
  }, []);

  // Re-resolved on DOM changes, not just at mount: this outlives the page offering the
  // slot (the dialog host sits outside the router), and holding a replaced element
  // would portal the notice into a detached tree. Null falls back to floating.
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  useEffect(() => {
    const resolve = () =>
      setSlot(document.getElementById(POPOUT_NOTICE_SLOT_ID));
    resolve();
    // Re-resolving the same element is a no-op setState, so this stays quiet.
    const observer = new MutationObserver(resolve);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const focusPopout = () => {
    try {
      if (!popout.win.closed) popout.win.focus();
    } catch {
      /* a window we no longer own can refuse focus */
    }
  };

  return ReactDOM.createPortal(
    <div css={[noticeCss, !slot && floatingCss]} role="status">
      <div css={textCss}>
        <span css={glyphCss} aria-hidden="true">
          ⇗
        </span>
        <span css={headingCss}>
          {getResource(resources, "POPOUT_NOTICE_HEADING", title)}
        </span>
        <span css={bodyCss}>{getResource(resources, "POPOUT_NOTICE_BODY")}</span>
      </div>
      <div css={buttonsCss}>
        <Buttons.SmallSecondary type="button" onClick={onDock}>
          {getResource(resources, "POPOUT_NOTICE_DOCK")}
        </Buttons.SmallSecondary>
        <Buttons.SmallNeutral type="button" onClick={focusPopout}>
          {getResource(resources, "POPOUT_NOTICE_SHOW_WINDOW")}
        </Buttons.SmallNeutral>
      </div>
    </div>,
    slot ?? document.body,
  );
}
